import {Injectable} from '@angular/core';
import {lastValueFrom, Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {StripeApiService} from './api/stripe-api.service';
import {
  BillingCycleType,
  CalculateActivePayload,
  IBillingDetails,
  ICalculateRequest,
  ICartItem,
  INewSubscriptionData,
  IPaymentCallbacks,
  IPaymentIntentStatus,
  IPaymentMethodRequest,
  IStripePaymentIntent,
  IStripeSubscription,
  IStripeSubscriptionResponse,
  ISwitchToAnnual,
  PaymentData,
  PdfPlanTier,
  ProductType,
  SharedPaymentLinkType,
  TierPlan,
} from '../types/payment.type';
import {ActivityTrackingService} from '../auth/shared/services/activity-tracking.service';
import {SettingsApiService} from './api/settings-api.service';
import {PaymentDataManager} from '../common/elf-store/payment-data/payment-data.manager';
import {ProductSubscription} from '../types/subscriptons.type';
import {
  BillingCalculateCartResponseDto,
  BillingCalculateCartResponseWithoutInvoiceDto,
  BillingCardWalletResponseDto,
  BillingPaymentMethodsListResponseDto,
  BillingRetryInvoiceResponseDto,
  CalculateCartBodyDto,
  CalculateCartTrialPayNowBodyDto,
  CartCalculateQuoteTotalsDto,
  CartRemediationUpgradeBodyDto,
  CheckPaymentMethodAddressBodyDto,
  CheckPaymentMethodAddressResponseDto,
  ConfirmInvoiceBodyDto,
  ConfirmSpecialPaymentBody,
  ConfirmSpecialPaymentResponse,
  CreateSubscriptionBodyDto,
  CreateUserSharedPaymentLinkDto,
  LaunchNoCardTrialSubscriptionDto,
  LaunchNoCardTrialSubscriptionResponseDto,
  LaunchQuoteSubscriptionDto,
  LaunchQuoteSubscriptionResponseDto,
  OneTimeChargeBodyDto,
  OneTimeChargeResponseDto,
  PaymentFormOrigin,
  PaymentMethodCreateBodyDto,
  PaymentMethodDetailsWithAddressDto,
  PaymentMethodUpdateBodyDto,
  PayOnTrialResponseDto,
  RetryInvoiceBodyDto,
  StripeCustomer,
  StripeInvoice,
  StripePaymentIntent,
  StripePaymentIntentStatus,
  SubscriptionBillingCycleType,
  UpgradeRemediationBody,
  UserwaySubjectTransactionType,
  V1BillingStripeTCartCalculateRemediationUpgradeCreateData,
} from '../../userway/sdk/UserWayApiClient';
import {StripeErrorMessage, StripeErrorsService} from './stripe-errors.service';
import {AmplitudeService} from './amplitude.service';
import {AmplitudeQuery} from '../common/elf-store/amplitude/amplitude.query';
import {PaymentDataQuery} from '../common/elf-store/payment-data/payment-data.query';
import {environment} from '../../environments/environment';
import {UserApiService} from './api/users-api.service';
import SetupIntent = stripe.setupIntents.SetupIntent;

type TrialProductType = UserwaySubjectTransactionType.REMEDIATION_PLUS | UserwaySubjectTransactionType.REMEDIATION;

@Injectable({
  providedIn: 'root',
})
export class StripeHelperService {
  tier: TierPlan;

  constructor(
    private stripeApi: StripeApiService,
    private ampliService: AmplitudeService,
    private amplitudeQuery: AmplitudeQuery,
    private paymentDataQuery: PaymentDataQuery,
    private paymentManager: PaymentDataManager,
    private settingsApiService: SettingsApiService,
    private stripeErrorService: StripeErrorsService,
    private activityTrackingService: ActivityTrackingService,
    private userApiService: UserApiService,
  ) {}

  getCustomer(identityHash?: string): Observable<StripeCustomer> {
    return this.stripeApi.getOrCreateCustomer(identityHash).pipe(
      map(response => {
        this.paymentManager.setCustomerId(response.payload.customer?.id);
        return response.payload.customer;
      }),
    );
  }

  getProducts(identityHash?: string): Observable<any> {
    return this.stripeApi.getProducts(identityHash).pipe(
      map(response => {
        return response.payload.products;
      }),
    );
  }

  getMultiPaymentMethods(): Observable<BillingPaymentMethodsListResponseDto> {
    return this.stripeApi.getMultiPaymentMethods().pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  addMultiPaymentMethods(data: PaymentMethodCreateBodyDto): Observable<any> {
    return this.stripeApi.addMultiPaymentMethods(data).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  updatePaymentMethod(paymentId: number, alias: string): Observable<any> {
    const data: PaymentMethodUpdateBodyDto = {alias};
    return this.stripeApi.updatePaymentMethod(paymentId, data).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  getSubscriptionsOfPaymentMethod(paymentId: number, searchBy?: string): Observable<ProductSubscription[]> {
    return this.stripeApi.getSubscriptionsOfPaymentMethod(paymentId, searchBy).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  setDefaultPaymentMethod(paymentId: number): Observable<any> {
    return this.stripeApi.setDefaultPaymentMethod(paymentId).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  removePaymentMethod(paymentId: number): Observable<any> {
    return this.stripeApi.removePaymentMethod(paymentId).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  getInvoiceToken(data: OneTimeChargeBodyDto): Observable<OneTimeChargeResponseDto> {
    return this.stripeApi.getInvoiceToken(data).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  getCardWallet(identityHash?: string): Observable<BillingCardWalletResponseDto> {
    return this.stripeApi.getCardWallet(identityHash).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  checkCardAddress(data: CheckPaymentMethodAddressBodyDto): Observable<CheckPaymentMethodAddressResponseDto> {
    return this.stripeApi.checkCardAddress(data).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  getDefaultPaymentMethod(): Observable<PaymentMethodDetailsWithAddressDto | null> {
    return this.stripeApi.getDefaultPaymentMethod().pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  getPaymentMethodBySubscriptionId(subscriptionId: number): Observable<PaymentMethodDetailsWithAddressDto | null> {
    return this.stripeApi.getPaymentMethodBySubscriptionId(subscriptionId).pipe(
      catchError(error => throwError(error)),
      map(({payload}: {payload: PaymentMethodDetailsWithAddressDto}) => {
        return payload;
      }),
    );
  }

  calculate(data: CalculateCartBodyDto, identityHash?: string): Observable<BillingCalculateCartResponseDto> {
    return this.stripeApi.calculate(data, identityHash).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  calculateRemediationUpgrade(
    data: CartRemediationUpgradeBodyDto,
    identityHash?: string,
  ): Observable<BillingCalculateCartResponseDto> {
    return this.stripeApi.calculateRemediationUpgrade(data, identityHash).pipe(
      catchError(error => throwError(error)),
      map((response: V1BillingStripeTCartCalculateRemediationUpgradeCreateData) => {
        return response.payload;
      }),
    );
  }

  updateRemediationProSubscription(
    data: UpgradeRemediationBody,
    identityHash: string,
  ): Observable<ConfirmSpecialPaymentResponse> {
    return this.stripeApi.updateRemediationProSubscription(data, identityHash).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  calculateSwitchPlan(data: ISwitchToAnnual): Observable<any> {
    return this.stripeApi.calculateSwitchPlan(data).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  calculateUpgradeRemediation(data: any): Observable<BillingCalculateCartResponseDto> {
    return this.stripeApi.calculateUpgradeRemediation(data).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  calculateOnTrial(data: CalculateCartTrialPayNowBodyDto): Observable<BillingCalculateCartResponseDto> {
    return this.stripeApi.calculateOnTrial(data).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  calculatePdfSubscription(requestData: ICalculateRequest): Observable<CalculateActivePayload> {
    return this.stripeApi.calculatePdfSubscription(requestData).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response.payload;
      }),
    );
  }

  startNoCardTrial(data: LaunchNoCardTrialSubscriptionDto): Observable<LaunchNoCardTrialSubscriptionResponseDto> {
    return this.userApiService.launchNoCardTrial(data).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  createQuoteSubscription(
    data: LaunchQuoteSubscriptionDto,
    identityHash?: string,
  ): Observable<LaunchQuoteSubscriptionResponseDto> {
    return this.stripeApi.createQuoteSubscription(data, identityHash).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  createAchSetupIntent(identityHash: string): Promise<{clientSecret: string}> {
    return lastValueFrom(this.stripeApi.createAchSetupIntent(identityHash).pipe(map(response => response.payload)));
  }

  async subscribeWithAch(data: Partial<PaymentData>) {
    const result = await lastValueFrom(
      this.createQuoteSubscription(
        {
          quoteHash: data.quoteHash,
          formOrigin: PaymentFormOrigin.MANAGE_QUOTE,
          paymentMethodId: data.paymentMethodId,
          billingCycle: data.billingCycle as SubscriptionBillingCycleType,
        },
        data.splIdentityHash,
      ).pipe(map(r => r as unknown as IStripeSubscriptionResponse)),
    ).catch(err => {
      this.stripeErrorService.handleCardError(err, data.onError);
      return null;
    });

    if (!result) return;

    const {subscription} = result;
    const pi = subscription?.latest_invoice?.payment_intent;

    // ACH: subscription is active; PaymentIntent lands in 'processing' — treat as success
    if (this.isActiveSubscription(subscription) || (pi as any)?.status === 'processing') {
      this.trackPurchaseEvent(subscription, data.billingCycle, null);
      data.onSuccess(result);
      return;
    }

    // requires_action means mandate confirmation is still needed
    if (pi?.status === IPaymentIntentStatus.requiresAction) {
      const confirmed = await (data.stripeInstance as any).confirmUsBankAccountPayment(pi.client_secret);
      if (confirmed.error) {
        data.setLatestInvoice(IPaymentIntentStatus.requiresAction, subscription.latest_invoice.id);
        data.onError({error: {message: confirmed.error.message}} as any);
        return;
      }
      data.onSuccess(result);
      return;
    }

    data.setLatestInvoice(IPaymentIntentStatus.requiresPaymentMethod, subscription.latest_invoice.id);
    data.onError(this.stripeErrorService.verifyInfoError);
  }

  calculateQuoteSubscription(
    data: CartCalculateQuoteTotalsDto,
    identityHash?: string,
  ): Observable<BillingCalculateCartResponseWithoutInvoiceDto> {
    return this.stripeApi.calculateQuote(data, identityHash).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  async upgradePdfRemediationPlan(
    requestData: Partial<PaymentData>,
    userwaySubscription: string,
  ): Promise<CalculateActivePayload> {
    const data: Partial<INewSubscriptionData> = {
      customerId: requestData.customerId,
      billingCycle: requestData.billingCycle,
      paymentMethodId: requestData.paymentMethodId,
      items: [
        {
          productId: requestData.productIds[0],
          sitesIds: requestData.sitesIds,
          settings: {
            pricing_tier: requestData.tiers,
            flowId: requestData.flowId,
            quantity: requestData.quantity,
          },
        },
      ],
      subscriptionId: requestData.subscriptionId,
    };
    const createNewSubscriptionResult = await this.stripeApi
      .updatePdfSubscription(data, userwaySubscription)
      .catch(err => {
        requestData.onError(this.stripeErrorService.verifyInfoError);
        this.stripeErrorService.showToastrOnError(StripeErrorMessage.INVALID_ADDRESS);
      });
    if (!createNewSubscriptionResult) {
      return;
    }

    requestData.onSuccess(createNewSubscriptionResult);
  }

  deletePaymentMethod(data: IPaymentMethodRequest): Observable<any> {
    return this.stripeApi.deletePaymentMethod(data).pipe(
      catchError(error => throwError(error)),
      map(response => {
        return response;
      }),
    );
  }

  createApiSubscription(
    data: CreateSubscriptionBodyDto,
    identityHash?: string,
    onError?: (event: any) => void,
  ): Promise<any> {
    return this.stripeApi
      .createNewSubscription(data, identityHash)
      .pipe(
        catchError(error => {
          onError(this.stripeErrorService.verifyInfoError);
          this.stripeErrorService.showToastrOnError(StripeErrorMessage.VERIFY_INFO);
          return throwError(error?.response?.data);
        }),
        map(response => response.payload),
      )
      .toPromise();
  }

  payOnTrial(userServiceId: number): Promise<PayOnTrialResponseDto> {
    return this.settingsApiService
      .payOnTrial(userServiceId)
      .pipe(catchError(error => throwError(error)))
      .toPromise();
  }

  createNewSubscriptionInvoice(data: any, identityHash?: string): Promise<any> {
    return this.stripeApi
      .createNewSubscriptionInvoice(data, identityHash)
      .pipe(
        catchError(error => throwError(error)),
        map(response => {
          return response.payload;
        }),
      )
      .toPromise();
  }

  confirmInvoice(data: ConfirmInvoiceBodyDto, identityHash?: string): Promise<any> {
    return this.stripeApi
      .confirmInvoice(data, identityHash)
      .pipe(
        catchError(error => throwError(error)),
        map(response => {
          return response.stripeSubscription;
        }),
      )
      .toPromise();
  }

  confirmSpecialPayment(
    data: ConfirmSpecialPaymentBody,
    splIdentityHash?: string,
  ): Observable<ConfirmSpecialPaymentResponse> {
    return this.stripeApi.confirmSpecialPayment(data, splIdentityHash).pipe(map(response => response.payload));
  }

  retryInvoice(data: RetryInvoiceBodyDto, identityHash?: string): Promise<any> {
    return this.stripeApi
      .retryInvoice(data, identityHash)
      .pipe(
        catchError(error => throwError(error)),
        map(response => response.payload),
      )
      .toPromise();
  }

  loadScript(onLoad: (_stripeInstance: stripe.Stripe) => void) {
    if (window.Stripe) {
      onLoad(window.Stripe(environment.stripeKey));
    } else {
      const stripeJs: HTMLScriptElement = document.createElement('script');
      stripeJs.src = 'https://js.stripe.com/v3/';
      stripeJs.onload = () => {
        onLoad(window.Stripe(environment.stripeKey));
      };
      document.head.appendChild(stripeJs);
    }
  }

  saveCard({
    stripeInstance,
    card,
    clientSecret,
    billingDetails,
    onError,
    onSuccess,
  }: {
    stripeInstance: stripe.Stripe;
    card: stripe.elements.Element;
    clientSecret: string;
    billingDetails: IBillingDetails;
    onError: (event: stripe.elements.ElementChangeResponse | stripe.PaymentMethodResponse | stripe.Error) => void;
    onSuccess: (setupIntent: SetupIntent) => void;
  }) {
    stripeInstance
      .confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: billingDetails,
        },
      })
      .then(({setupIntent, error}) => {
        if (error) {
          this.stripeErrorService.handleCardError(error, onError);
        } else if (setupIntent.status === 'succeeded') {
          onSuccess(setupIntent);
        }
      });
  }

  subscribe(data: Partial<PaymentData>) {
    return this.createPaymentMethod(data as PaymentData);
  }

  async updatePaymentMethodAndRetryInvoiceCharge({
    stripeInstance,
    subscriptionId,
    card,
    splIdentityHash,
    billingDetails,
    onError,
    onSuccess,
  }: {
    stripeInstance: stripe.Stripe;
    subscriptionId: string;
    card: stripe.elements.Element;
    splIdentityHash: string;
    billingDetails: IBillingDetails;
    onError: (event: any) => void;
    onSuccess: (response: any) => void;
  }) {
    const result = await stripeInstance.createPaymentMethod({
      type: 'card',
      card,
      billing_details: billingDetails,
    });

    if (result.error) {
      return onError(result);
    }

    const paymentMethodId = result.paymentMethod.id;
    const retryRes = (await this.retryInvoice(
      {
        paymentMethodId,
        subscriptionId,
      },
      splIdentityHash,
    ).catch(e => {
      return onError(e);
    })) as BillingRetryInvoiceResponseDto;

    const invoice = retryRes.invoice as StripeInvoice;
    if (!invoice) {
      return onError(null);
    }

    if (
      this.isStripePaymentIntent(invoice.payment_intent) &&
      invoice.payment_intent.status === StripePaymentIntentStatus.RequiresAction
    ) {
      const paymentIntentResponse = await stripeInstance.confirmCardPayment(invoice.payment_intent.client_secret, {
        payment_method: paymentMethodId,
      });

      if (paymentIntentResponse.error) {
        onError(this.stripeErrorService.verifyInfoError);
      }
    }

    onSuccess(retryRes);
  }

  isStripePaymentIntent(intent: string | StripePaymentIntent | null): intent is StripePaymentIntent {
    return !!intent && typeof intent !== 'string';
  }

  generatePaymentLink(data: CreateUserSharedPaymentLinkDto): Observable<string> {
    return this.stripeApi.generatePaymentLink(data).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  async createPaymentMethod(data: Partial<PaymentData>) {
    let {paymentMethodId, stripeInstance, card, billingDetails} = data;

    if (!paymentMethodId) {
      const result = await stripeInstance.createPaymentMethod({
        type: 'card',
        card,
        billing_details: billingDetails,
      });
      if (result.error) {
        return data.onError(result);
      } else {
        paymentMethodId = result.paymentMethod.id;
      }
    }

    if (!paymentMethodId) {
      return; // cannot proceed without a payment method id
    }

    // Payment retry flow
    if (data.isPaymentRetry) {
      return this.retryInvoiceWithNewPaymentMethod(
        data.customerId,
        paymentMethodId,
        data.invoiceId,
        data.origin,
        data.splIdentityHash,
        data.setLatestInvoice,
        data.onError,
        data.onSuccess,
        data.subscriptionIdNumber,
      );
    }

    // Create the subscription
    const selectedProduct = data.selectedProduct;
    if (!data.orderId || (data.flowId && selectedProduct === ProductType.PdfAutoRemediation)) {
      // Forward the whole data object, overriding paymentMethodId with the final value
      await this.createSubscription({
        ...data,
        paymentMethodId,
      });
    } else {
      await this.createSubscriptionForPdfOrder({
        customerId: data.customerId,
        paymentMethodId: paymentMethodId as any,
        productIds: data.productIds,
        billingCycle: data.billingCycle,
        setLatestInvoice: data.setLatestInvoice,
        onError: data.onError,
        onSuccess: data.onSuccess,
        splIdentityHash: data.splIdentityHash,
        stripeInstance: data.stripeInstance,
        origin: data.origin,
        trial: data.trial,
        coupon: data.coupon,
        liveTranslationsPlan: data.liveTranslationsPlan,
        cancelRunningSubscription: data.cancelRunningSubscription,
        orderId: data.orderId,
        tiers: data.tiers,
      });
    }
  }

  private async createSubscription(data: Partial<PaymentData>) {
    const {
      customerId,
      subscriptionIdNumber,
      userServiceId,
      paymentMethodId,
      productIds,
      sitesIds,
      billingCycle,
      setLatestInvoice,
      onError,
      onSuccess,
      splIdentityHash,
      quoteHash,
      stripeInstance,
      origin,
      trial,
      coupon,
      liveTranslationsPlan,
      cancelRunningSubscription,
      isPaymentOnTrial,
      items = [],
      orderId,
      flowId,
      tiers,
      quantity,
      isNoCardTrial,
      selectedProduct,
    } = data;
    const requestData = this.prepareSubscriptionData({
      customerId,
      origin,
      billingCycle,
      paymentMethodId,
      cancelRunningSubscription,
      trial,
      coupon,
      items,
      productIds,
      sitesIds,
      liveTranslationsPlan,
      orderId,
      flowId,
      tiers,
      quantity,
    });

    if (origin === SharedPaymentLinkType.UPGRADE_TO_PRO_PLUS) {
      return this.handleUpgradeProToPlus(
        stripeInstance,
        subscriptionIdNumber,
        paymentMethodId,
        splIdentityHash,
        setLatestInvoice,
        onError,
        onSuccess,
      );
    }

    const upsertApiSubscriptionWithOriginCheck = async (): Promise<IStripeSubscriptionResponse> => {
      if (origin === SharedPaymentLinkType.QUOTE) {
        return this.createQuoteSubscription(
          {
            quoteHash,
            formOrigin: PaymentFormOrigin.MANAGE_QUOTE,
            paymentMethodId,
            billingCycle: billingCycle as SubscriptionBillingCycleType,
          },
          splIdentityHash,
        )
          .pipe(map(result => result as unknown as IStripeSubscriptionResponse))
          .toPromise();
      }

      if (isNoCardTrial) {
        return this.startNoCardTrial({
          billingCycle: billingCycle as SubscriptionBillingCycleType,
          formOrigin: origin as PaymentFormOrigin,
          paymentMethodId,
          couponId: coupon,
          productType: selectedProduct as unknown as TrialProductType,
        })
          .pipe(map(result => result as unknown as IStripeSubscriptionResponse))
          .toPromise();
      }

      if (isPaymentOnTrial) {
        return (await this.handlePaymentOnTrial(userServiceId, onError)) as unknown as IStripeSubscriptionResponse;
      }

      return this.createApiSubscription(requestData, splIdentityHash, onError);
    };

    const upsertNewSubscriptionResult = await upsertApiSubscriptionWithOriginCheck().catch(err => {
      this.stripeErrorService.handleCardError(err, onError);
    });

    if (!upsertNewSubscriptionResult) {
      return;
    }

    const {subscription, user} = upsertNewSubscriptionResult;

    this.trackTrialEvents(trial);

    if (this.isActiveSubscription(subscription) && !isPaymentOnTrial) {
      this.trackPurchaseEvent(subscription, billingCycle, coupon);
      onSuccess(upsertNewSubscriptionResult);
      return;
    }

    await this.handlePaymentIntent(
      subscription,
      stripeInstance,
      paymentMethodId,
      origin,
      splIdentityHash,
      user,
      isPaymentOnTrial,
      setLatestInvoice,
      onError,
      onSuccess,
    );
  }

  private prepareSubscriptionData({
    customerId,
    origin,
    billingCycle,
    paymentMethodId,
    cancelRunningSubscription,
    trial,
    coupon,
    items,
    productIds,
    sitesIds,
    liveTranslationsPlan,
    orderId,
    flowId,
    tiers,
    quantity,
  }): CreateSubscriptionBodyDto {
    const data: CreateSubscriptionBodyDto = {
      customerId,
      formOrigin: origin as PaymentFormOrigin,
      billingCycle: billingCycle as SubscriptionBillingCycleType,
      paymentMethodId,
      cancelRunningSubscription,
      trial,
      coupon,
      items,
    };

    productIds?.forEach(productId => {
      const item: ICartItem = {
        productId,
        sitesIds,
      };

      if (liveTranslationsPlan) {
        item.settings = {pricing_tier: liveTranslationsPlan};
      }
      if (this.tier) {
        item.settings = {pricing_tier: this.tier};
      }
      if (tiers && orderId) {
        item.settings = {
          pricing_tier: tiers,
          pdfRemediationOrderId: orderId,
        };
      }
      if (flowId) {
        item.settings = {
          pricing_tier: tiers,
          flowId,
        };
      }
      this.tier = null;

      if (item.settings?.pricing_tier === PdfPlanTier.CUSTOM && quantity) {
        item.settings.quantity = quantity;
      }

      data.items.push(item);
    });

    return data;
  }

  private async handlePaymentOnTrial(userServiceId: number, onError: (error: any) => void) {
    return await this.payOnTrial(userServiceId).catch(err => {
      this.stripeErrorService.handleCardError(err, onError);
    });
  }

  private handleUpgradeProToPlus(
    stripeInstance: stripe.Stripe,
    subscriptionId: number,
    paymentMethodId: string,
    splIdentityHash: string,
    setLatestInvoice: IPaymentCallbacks['setLatestInvoice'],
    onError: IPaymentCallbacks['onError'],
    onSuccess: IPaymentCallbacks['onSuccess'],
  ): void {
    const body: UpgradeRemediationBody = {paymentMethodId, subscriptionId};
    const observable$ = this.updateRemediationProSubscription(body, splIdentityHash);
    this.handleSpecialPaymentObservable(
      observable$,
      stripeInstance,
      paymentMethodId,
      splIdentityHash,
      setLatestInvoice,
      onError,
      onSuccess,
    );
  }

  private handleSpecialPaymentObservable(
    observable$: Observable<ConfirmSpecialPaymentResponse>,
    stripeInstance: stripe.Stripe,
    paymentMethodId: string,
    splIdentityHash: string,
    setLatestInvoice: IPaymentCallbacks['setLatestInvoice'],
    onError: IPaymentCallbacks['onError'],
    onSuccess: IPaymentCallbacks['onSuccess'],
  ): void {
    observable$.subscribe({
      next: specialPaymentResponse => {
        return this.handleSpecialPaymentResponse(
          stripeInstance,
          specialPaymentResponse,
          paymentMethodId,
          splIdentityHash,
          setLatestInvoice,
          onError,
          onSuccess,
        );
      },
      error: err => {
        this.stripeErrorService.handleCardError(err, onError);
      },
    });
  }

  private trackTrialEvents(isTrial: boolean) {
    if (isTrial && !this.paymentDataQuery.isPaymentLink) {
      this.activityTrackingService.startTrialEvent();
      this.ampliService.startTrial({
        trialProductName: this.amplitudeQuery.purchaseProperties.purchaseProductName,
        trialTriggeredPlace: this.amplitudeQuery.purchaseProperties.purchaseTriggeredPlace,
        trialTriggeredPage: this.amplitudeQuery.purchaseProperties.purchaseTriggeredPage,
      });
    }
  }

  private isActiveSubscription(subscription: IStripeSubscription): boolean {
    return subscription && (subscription.status === 'active' || subscription.status === 'trialing');
  }

  private trackPurchaseEvent(subscription: IStripeSubscription, billingCycle: BillingCycleType, coupon: string) {
    this.activityTrackingService.pushToDataLayer({
      event: 'purchase',
      kind: 'manage',
      gtm_trigger: 'true',
      currency: subscription.latest_invoice.currency,
      transaction_id: subscription.id,
      value: subscription.latest_invoice.total / 100,
      plan_type: billingCycle,
      coupon_code: coupon,
    });
  }

  async handlePaymentIntent(
    subscription: IStripeSubscription,
    stripeInstance: stripe.Stripe,
    paymentMethodId: string,
    origin: string,
    splIdentityHash: string,
    user: any,
    isPaymentOnTrial: boolean,
    setLatestInvoice: (status: IPaymentIntentStatus, invoiceId: string) => void,
    onError: (error: any) => void,
    onSuccess: (result: any) => void,
  ) {
    const paymentIntent: IStripePaymentIntent = subscription.latest_invoice.payment_intent;
    const status = paymentIntent.status;

    if (status === IPaymentIntentStatus.requiresAction) {
      await this.handlePaymentThatRequiresCustomerAction(
        stripeInstance,
        subscription,
        paymentIntent,
        paymentMethodId,
        origin,
        splIdentityHash,
        user,
        isPaymentOnTrial,
        setLatestInvoice,
        onError,
        onSuccess,
      );
    } else if (isPaymentOnTrial) {
      onSuccess({subscription});
    } else {
      setLatestInvoice(IPaymentIntentStatus.requiresPaymentMethod, subscription.latest_invoice.id);
      onError(this.stripeErrorService.verifyInfoError);
    }
  }

  private async createSubscriptionForPdfOrder({
    customerId,
    paymentMethodId,
    productIds,
    billingCycle,
    onError,
    onSuccess,
    splIdentityHash,
    origin,
    trial,
    coupon,
    orderId,
    tiers,
  }: {
    customerId: string;
    paymentMethodId: number;
    productIds: string[];
    onError: (error: any) => void;
    onSuccess: (success: any) => void;
    setLatestInvoice: (latestInvoicePaymentIntentStatus: IPaymentIntentStatus, latestInvoiceId: string) => void;
    splIdentityHash?: string;
    stripeInstance: stripe.Stripe;
    trial: boolean;
    coupon: string;
    origin: string;
    liveTranslationsPlan: TierPlan;
    cancelRunningSubscription: boolean | null;
    orderId?: number;
    billingCycle: BillingCycleType;
    tiers: TierPlan;
  }) {
    const requestData = {
      formOrigin: origin,
      customerId,
      paymentMethodId,
      billingCycle,
      coupon,
      trial,
      items: [
        {
          productId: productIds[0],
          sitesIds: [],
          settings: {
            pdfRemediationOrderId: orderId,
            pricing_tier: tiers,
          },
        },
      ],
    };

    const createNewSubscriptionResult = await this.createNewSubscriptionInvoice(requestData, splIdentityHash).catch(
      err => {
        this.stripeErrorService.handleCardError(err, onError);
      },
    );

    if (!createNewSubscriptionResult) {
      return;
    }

    onSuccess(createNewSubscriptionResult);
  }

  private async handleSpecialPaymentResponse(
    stripeInstance: stripe.Stripe,
    specialPaymentResponse: ConfirmSpecialPaymentResponse,
    paymentMethodId: string,
    splIdentityHash: string,
    setLatestInvoice: IPaymentCallbacks['setLatestInvoice'],
    onError: IPaymentCallbacks['onError'],
    onSuccess: IPaymentCallbacks['onSuccess'],
  ): Promise<void> {
    if (specialPaymentResponse.payment.status === 'paid') {
      return onSuccess();
    }

    const paymentIntentResponse = await stripeInstance.confirmCardPayment(
      specialPaymentResponse.payment.paymentSecret,
      {
        payment_method: paymentMethodId,
      },
    );

    if (paymentIntentResponse.error) {
      return this.handlePaymentAuthorizationError(setLatestInvoice, onError, specialPaymentResponse.payment.paymentId);
    }

    if (paymentIntentResponse?.paymentIntent?.status === 'succeeded') {
      await lastValueFrom(
        this.confirmSpecialPayment({paymentId: specialPaymentResponse.payment.paymentId}, splIdentityHash),
      );
      onSuccess();
    } else {
      setLatestInvoice(IPaymentIntentStatus.requiresAction, specialPaymentResponse.payment.paymentId);
      onError(this.stripeErrorService.verifyInfoError);
    }
  }

  private async handlePaymentThatRequiresCustomerAction(
    stripeInstance,
    subscription,
    paymentIntent,
    paymentMethodId,
    origin,
    splHash,
    user,
    isPaymentOnTrial: boolean,
    setLatestInvoice,
    onError,
    onSuccess,
  ) {
    const paymentIntentResponse = await stripeInstance.confirmCardPayment(paymentIntent.client_secret, {
      payment_method: paymentMethodId,
    });

    if (paymentIntentResponse.error) {
      return this.handlePaymentAuthorizationError(setLatestInvoice, onError, subscription.latest_invoice.id);
    }

    if (paymentIntentResponse?.paymentIntent?.status === 'succeeded') {
      if (isPaymentOnTrial) {
        onSuccess({subscription});
        return;
      }
      const _subscription = await this.confirmInvoice(
        {
          paymentMethodId,
          subscriptionId: subscription.id,
          formOrigin: origin,
        },
        splHash,
      );
      onSuccess({subscription: _subscription, user});
    } else {
      setLatestInvoice(IPaymentIntentStatus.requiresAction, subscription.latest_invoice.id);
      onError({error: {message: 'Your card was not authorized.'}});
    }
  }

  private handlePaymentAuthorizationError(
    setLatestInvoice: IPaymentCallbacks['setLatestInvoice'],
    onError: IPaymentCallbacks['onError'],
    paymentId: string,
  ) {
    setLatestInvoice(IPaymentIntentStatus.requiresAction, paymentId);
    this.stripeErrorService.showToastrOnError(StripeErrorMessage.PAYMENT_DECLINED);
    return onError({error: this.stripeErrorService.cardError});
  }

  private async retryInvoiceWithNewPaymentMethod(
    customerId,
    paymentMethodId,
    invoiceId,
    origin,
    splIdentityHash,
    setLatestInvoice,
    onError,
    onSuccess,
    subscriptionIdNumber,
  ) {
    const data = {
      subscriptionId: subscriptionIdNumber,
      paymentMethodId: paymentMethodId,
    };

    const retryInvoiceResponse = await this.retryInvoice(data, splIdentityHash).catch(beErrorResponse => {
      console.error(beErrorResponse.error.error.message);
      onError(this.stripeErrorService.verifyInfoError);
    });

    const invoice = retryInvoiceResponse.invoice;
    if (invoice.status !== 'paid') {
      setLatestInvoice(IPaymentIntentStatus.requiresPaymentMethod, invoice.id);
      onError(this.stripeErrorService.verifyInfoError);
    }
    onSuccess({subscription: retryInvoiceResponse.subscription, user: retryInvoiceResponse.user});
  }
}
